import React from "react";
import type { FieldError, FieldValues, Path, RegisterOptions, UseFormRegister } from "react-hook-form";
import "../../styles/checkbox-input.scss";

interface CheckboxInputProps<TFormValues extends FieldValues = FieldValues> {
  id: string;
  name: Path<TFormValues>;
  label: React.ReactNode;
  value?: string;
  className?: string;
  inputClassName?: string;
  register?: UseFormRegister<TFormValues>;
  validation?: RegisterOptions<TFormValues, Path<TFormValues>>;
  error?: FieldError;
  invalid?: boolean;
  ariaDescribedBy?: string;
  checked?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
}

const CheckboxInput = <TFormValues extends FieldValues = FieldValues>({
  id,
  name,
  label,
  value,
  className = "",
  inputClassName = "",
  register,
  validation,
  error,
  invalid = false,
  ariaDescribedBy,
  checked,
  onChange,
  disabled = false,
}: CheckboxInputProps<TFormValues>) => {
  const registration = register ? register(name, validation) : undefined;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    registration?.onChange(e);
    onChange?.(e);
  };

  return (
    <label className={`gc-checkbox ${className}`.trim()} htmlFor={id}>
      <span className="gc-checkbox__control">
        <input
          type="checkbox"
          id={id}
          className={inputClassName}
          name={registration?.name ?? name}
          value={value}
          checked={checked}
          onChange={handleChange}
          onBlur={registration?.onBlur}
          ref={registration?.ref}
          disabled={disabled}
          aria-describedby={ariaDescribedBy}
          aria-invalid={error || invalid ? "true" : undefined}
        />
      </span>
      <div className="gcw:flex gcw:flex-col gcw:wrap-anywhere">
        {label}
        {error && <div className="gcw:text-error gcw:text-md gcw:mt-1">{error.message}</div>}
      </div>
    </label>
  );
};

export default CheckboxInput;
